import React from 'react';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import {
  Image as ImageIcon,
  ShoppingCart as ShoppingCartIcon,
  LockOpen as LockOpenIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Bookmark as BookmarkIcon,
  Visibility as VisibilityIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import type { PublicUnitForMenu } from './types';
import { FULL_VERSION_MENU_CAPTION } from '../FullVersionTooltip';

/**
 * Actions carried over from the agent's unit menu
 * (`TopViewingSitePlanDetail.tsx`) that don't exist in the public
 * ("lite") app. They're rendered as disabled rows with an inline caption
 * below the label — matching the agent menu's `ListItemText.secondary`
 * pattern (e.g. `*Not available for external users`).
 */
const GATED_MENU_ITEMS: ReadonlyArray<{ key: string; label: string; icon: React.ReactNode }> = [
  { key: 'reference', label: 'Set as Reference Unit', icon: <BookmarkIcon fontSize="small" /> },
  { key: 'blinker', label: 'Set as Anticipate Blinker Unit', icon: <VisibilityIcon fontSize="small" /> },
  { key: 'shortlist', label: 'Add to Shortlist', icon: <StarBorderIcon fontSize="small" /> },
];

/** Disabled menu row with inline full-version caption (matches agent secondary-text pattern). */
const GatedMenuItem: React.FC<{ label: string; icon: React.ReactNode }> = ({ label, icon }) => (
  <MenuItem disabled sx={{ alignItems: 'flex-start' }}>
    <ListItemIcon sx={{ mt: 0.25 }}>{icon}</ListItemIcon>
    <ListItemText
      primary={label}
      secondary={FULL_VERSION_MENU_CAPTION}
      secondaryTypographyProps={{ variant: 'caption' }}
    />
  </MenuItem>
);

/**
 * Anchor position state for the public unit-actions menu. Mirrors the
 * agent's `topViewingUnitMenuAnchor` / `sectionUnitMenuAnchor` state shape
 * in `TopViewingSitePlanDetail.tsx` — coordinates are absolute viewport
 * coordinates from a `MouseEvent`.
 */
export interface UnitMenuAnchor {
  unit: PublicUnitForMenu;
  mouseX: number;
  mouseY: number;
}

interface UnitActionsMenuProps {
  anchor: UnitMenuAnchor | null;
  onClose: () => void;
  /**
   * Omit to hide the "View Floor Plan" item — used by call sites that
   * surface the menu purely for the per-row Reveal Prices flow (e.g.
   * TowerView grid cells).
   */
  onViewFloorPlan?: (unit: PublicUnitForMenu) => void;
  /**
   * Omit to hide the "Add to Compare" item — same rationale as
   * `onViewFloorPlan`.
   */
  onToggleCompare?: (unit: PublicUnitForMenu) => void;
  /**
   * Optional callback wired by share-link recipients. When supplied, the
   * menu renders a "Reveal prices (1 token)" item that spends one token
   * to unmask this specific unit's prices. Omit to hide the item entirely
   * (e.g. agent EXTERNAL sessions, or rows whose unit has no DB-side
   * pricing to reveal).
   */
  onRevealPrices?: (unit: PublicUnitForMenu) => void;
  /**
   * `true` when the unit's prices have already been revealed in this
   * session — the menu shows a disabled "Prices revealed" indicator
   * instead of the spend CTA so the recipient doesn't pay twice.
   */
  isPricesRevealed?: boolean;
  isInCart: (unitKey: string, projectId?: string | null) => boolean;
  cartSize: number;
  capacity: number;
}

/**
 * Public-side replica of the agent's top-viewing unit-action menu
 * (`TopViewingSitePlanDetail.tsx`, the `<Menu anchorReference="anchorPosition">`
 * block around line 11400). Layout and copy mirror the agent's item list,
 * but only `View Floor Plan` and `Add to Compare` are functional — the
 * agent-only actions render as disabled rows with an inline caption. The
 * share-link `Reveal prices` item is a public-only feature and stays functional.
 */
export const UnitActionsMenu: React.FC<UnitActionsMenuProps> = ({
  anchor,
  onClose,
  onViewFloorPlan,
  onToggleCompare,
  onRevealPrices,
  isPricesRevealed = false,
  isInCart,
  cartSize,
  capacity,
}) => {
  const unit = anchor?.unit ?? null;
  const inCart = unit ? isInCart(unit.unitKey, unit.projectId) : false;
  const cartFull = !inCart && cartSize >= capacity;
  const noFloorplan = !unit?.floorplan;

  return (
    <Menu
      anchorReference="anchorPosition"
      anchorPosition={anchor ? { top: anchor.mouseY, left: anchor.mouseX } : undefined}
      open={Boolean(anchor)}
      onClose={onClose}
    >
      <MenuItem disabled sx={{ opacity: 1 }}>
        <ListItemText
          primary={`Unit ${unit?.unitNumber ?? '-'}`}
          secondary={unit?.projectName ?? ''}
          primaryTypographyProps={{ sx: { fontWeight: 700, color: 'var(--color-text-strong)' } }}
          secondaryTypographyProps={{ sx: { color: 'var(--color-text-muted)' } }}
        />
      </MenuItem>
      <Divider />
      {onViewFloorPlan && (
        <MenuItem
          disabled={noFloorplan}
          onClick={() => {
            if (!unit) return;
            onViewFloorPlan(unit);
            onClose();
          }}
        >
          <ListItemIcon>
            <ImageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Floor Plan</ListItemText>
        </MenuItem>
      )}
      <GatedMenuItem
        label={GATED_MENU_ITEMS[0].label}
        icon={GATED_MENU_ITEMS[0].icon}
      />
      <GatedMenuItem
        label={GATED_MENU_ITEMS[1].label}
        icon={GATED_MENU_ITEMS[1].icon}
      />
      {onToggleCompare && (
        <MenuItem
          disabled={noFloorplan || cartFull}
          onClick={() => {
            if (!unit) return;
            onToggleCompare(unit);
            onClose();
          }}
        >
          <ListItemIcon>
            <ShoppingCartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {inCart ? 'Remove from Compare' : `Add to Compare (${cartSize}/${capacity})`}
          </ListItemText>
        </MenuItem>
      )}
      <GatedMenuItem
        label={GATED_MENU_ITEMS[2].label}
        icon={GATED_MENU_ITEMS[2].icon}
      />

      {onRevealPrices && (
        <>
          {(onViewFloorPlan || onToggleCompare) && <Divider />}
          {isPricesRevealed ? (
            <MenuItem disabled sx={{ opacity: 1 }}>
              <ListItemIcon>
                <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'rgb(46, 160, 67)' }} />
              </ListItemIcon>
              <ListItemText
                primary="Prices revealed"
                primaryTypographyProps={{
                  sx: { color: 'rgb(46, 160, 67)', fontWeight: 600 },
                }}
              />
            </MenuItem>
          ) : (
            <MenuItem
              onClick={() => {
                if (!unit) return;
                onRevealPrices(unit);
                onClose();
              }}
            >
              <ListItemIcon>
                <LockOpenIcon fontSize="small" sx={{ color: 'var(--color-accent)' }} />
              </ListItemIcon>
              <ListItemText
                primary="Reveal prices"
                secondary="1 token"
                primaryTypographyProps={{
                  sx: { color: 'var(--color-accent)', fontWeight: 600 },
                }}
                secondaryTypographyProps={{
                  sx: { color: 'var(--color-text-muted)' },
                }}
              />
            </MenuItem>
          )}
        </>
      )}
    </Menu>
  );
};

export default UnitActionsMenu;
